import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
  Box,
  Paper,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useQuery } from "@tanstack/react-query";
import { getAllMerchantPrinters } from "apis/printAPIs";

// API function to get all merchants' printers

// Helper function to get printer type from deviceType code
const getPrinterTypeLabel = (deviceType) => {
  switch (Number(deviceType)) {
    case 0: return "Unknown";
    case 1: return "Network";
    case 2: return "Bluetooth";
    case 3: return "USB";
    case 4: return "Serial";
    default: return "Other";
  }
};

const Printers = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentTab, setCurrentTab] = useState(0);
  
  const { data: merchantPrinterData, isLoading } = useQuery({
    queryKey: ["merchantPrinters"],
    queryFn: getAllMerchantPrinters
  });

  // Flatten merchants and their printers for display
  const flattenedPrinters = useMemo(() => {
    if (!merchantPrinterData) return [];
    
    return merchantPrinterData.flatMap(restaurant => 
      restaurant.printers.map(printer => ({
        ...printer,
        restaurantId: restaurant.restaurantId,
        id: `${restaurant.restaurantId}-${printer.macAddress || printer.ipAddress}`
      }))
    );
  }, [merchantPrinterData]);

  if (isLoading) return <LinearProgress />;

  // Calculate summary statistics
  const deviceSummary = flattenedPrinters.reduce((acc, printer) => {
    const brand = printer.brand || "unknown";
    acc[brand] = (acc[brand] || 0) + 1;
    return acc;
  }, {});

  // Calculate device type summary
  const deviceTypeSummary = flattenedPrinters.reduce((acc, printer) => {
    const deviceName = printer.deviceName;
    acc[deviceName] = (acc[deviceName] || 0) + 1;
    return acc;
  }, {});

  // Filter printers based on search term
  const filteredPrinters = flattenedPrinters.filter(printer => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    return (
      (printer.restaurantId || '').toLowerCase().includes(search) ||
      (printer.printerName || '').toLowerCase().includes(search) ||
      (printer.deviceName || '').toLowerCase().includes(search) ||
      (printer.ipAddress || '').toLowerCase().includes(search) ||
      (printer.brand || '').toLowerCase().includes(search) ||
      (printer.macAddress || '').toLowerCase().includes(search)
    );
  });

  // Group printers by restaurant for the table display
  const groupedPrinters = filteredPrinters.reduce((acc, printer) => {
    const restaurantId = printer.restaurantId;
    if (!acc[restaurantId]) {
      acc[restaurantId] = [];
    }
    acc[restaurantId].push(printer);
    return acc;
  }, {});

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Render Summary Tab Content
  const renderSummaryTab = () => (
    <Box>
      {/* Device Type Summary Table */}
      <Card variant="outlined" style={{ marginBottom: 24 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Device Type Summary
          </Typography>
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Device Type</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deviceTypeSummary && Object.entries(deviceTypeSummary)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count]) => (
                  <TableRow key={type}>
                    <TableCell>{type}</TableCell>
                    <TableCell align="right">{count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </CardContent>
      </Card>

      {/* Brand Summary Table */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Brand Summary
          </Typography>
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Brand</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deviceSummary && Object.entries(deviceSummary)
                  .sort((a, b) => b[1] - a[1])
                  .map(([brand, count]) => (
                  <TableRow key={brand}>
                    <TableCell>{brand}</TableCell>
                    <TableCell align="right">{count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </CardContent>
      </Card>
    </Box>
  );

  // Render Details Tab Content
  const renderDetailsTab = () => (
    <Box>
      {/* Search Field */}
      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by merchant, printer name, device, IP address, brand, or MAC address"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Printers Table */}
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Merchant</TableCell>
              <TableCell>Printer Name</TableCell>
              <TableCell>Device Name</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(groupedPrinters).length > 0 ? (
              Object.entries(groupedPrinters).map(([restaurantId, printers]) => (
                printers.map((printer, index) => (
                  <TableRow key={printer.id}>
                    {index === 0 ? (
                      <TableCell rowSpan={printers.length}>{restaurantId}</TableCell>
                    ) : null}
                    <TableCell>{printer.printerName || printer.deviceName}</TableCell>
                    <TableCell>
                      {printer.deviceName}
                      <Chip
                        label={getPrinterTypeLabel(printer.deviceType)}
                        size="small"
                        style={{ marginLeft: 8 }}
                      />
                    </TableCell>
                    <TableCell>{printer.ipAddress}</TableCell>
                    <TableCell>
                      <Chip 
                        label={printer.brand || "unknown"} 
                        size="small"
                        color={printer.brand ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={printer.isPrimary ? "Primary" : "Secondary"} 
                        size="small"
                        color={printer.isPrimary ? "secondary" : "default"}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body1">
                    {searchTerm ? "No printers match your search criteria" : "No printers found"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );

  return (
    <Box padding={3}>
      <Typography variant="h4" gutterBottom>
        Merchant Printers
      </Typography>

      <Paper style={{ marginBottom: 24 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Summary" />
          <Tab label="Details" />
        </Tabs>
      </Paper>

      {currentTab === 0 ? renderSummaryTab() : renderDetailsTab()}
    </Box>
  );
};

export default Printers; 